import React, { FormEvent, useCallback } from 'react';
import { TextInput } from '@patternfly/react-core';

import './text-override-field.css';
import InputLabel from './input-label';
import DebouncedTextInput from './debounced-text-input';
import { PatternFlyValidated } from './validation-types';

interface TextOverrideFieldProps {
    defaultText: string,
    textValue: string,
    onChange?: (value: string) => void,
    id: string,
    /** Text to show to user when text is overridden. Leave undefined to essentially disable this component's main use. */
    textIsOverriddenHelp?: React.ReactNode,
    validated?: PatternFlyValidated | undefined,
    isDisabled?: boolean | undefined;
    /** read-only components are like disabled but their font color is brighter to make them easier to read */
    isReadOnly?: boolean,
    className?: string | undefined;
    label?: string;
    allowEmptyField?: boolean;
    placeholder?: string;
}

const TextOverrideField = (props: TextOverrideFieldProps) => {
    const { id, defaultText, textValue, onChange, textIsOverriddenHelp, validated, isDisabled, label, allowEmptyField, isReadOnly, placeholder } = props;


    const handleChange = useCallback((value: string) => {
        if (onChange) {
            onChange(value);
        }
    }, [onChange]);

    const handleBlur = useCallback(() => {
        // revert an empty text field back to default value when focus leaves
        if (allowEmptyField === false && !textValue) {
            handleChange(defaultText);
        }
    }, [textValue, defaultText, handleChange, allowEmptyField]);

    const currentValue = textValue !== undefined && textValue !== null ? textValue : defaultText;
    const className = `text-override-field ${props.className || ''}`;

    return (
        <div className={className}>
            <InputLabel label={label} useRef>
                <DebouncedTextInput
                    className={`text-override-input ${isReadOnly ? 'is-read-only' : ''}`}
                    type="text"
                    fieldId={id}
                    onDebouncedChange={handleChange}
                    defaultValue={currentValue}
                    validated={validated}
                    onBlur={handleBlur}
                    isDisabled={isDisabled === true || isReadOnly === true}
                    placeholder={placeholder}
                />
            </InputLabel>
            {textIsOverriddenHelp && currentValue && currentValue !== defaultText && (
                <div className="text-is-overridden">{textIsOverriddenHelp}</div>
            )}
        </div>
    );
}

export default TextOverrideField;